import { AxiosError, AxiosProgressEvent, AxiosResponse, CancelTokenSource } from 'axios';
import { eventBus, subscribe } from 'mobx-event-bus2';

import { action, runInAction, makeObservable, observable } from 'mobx';

import DetailsActivities from 'ui/stores/DetailsActivities';
import { ActivityListResponse, FileActivityResponse } from 'ui/types';

import Logger from 'utils/logger';

import API from 'utils/api';
import RootStore from 'stores/Root';
import { EventType } from 'utils/events/constants';
import { postMessage } from 'utils/events/broadcast';

export default class InspectionActivities extends DetailsActivities<RootStore, typeof API> {
  inspectionId: string;

  @observable attachments: FileActivityResponse[] = [];

  @observable loadingAttachments = false;

  @observable deletingFile: string | null = null;

  constructor(rootStore: RootStore, api: typeof API, inspectionId: string) {
    super(rootStore, api);

    makeObservable(this);

    this.inspectionId = inspectionId;

    eventBus.register(this);
  }

  async loadActivitiesData(): Promise<AxiosResponse<ActivityListResponse>> {
    return API.workflows.loadInspectionAttachments(this.inspectionId)();
  }

  async doUploadFile(
    file: File,
    onUpdateProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): Promise<void> {
    try {
      const { data } = await API.workflows.uploadInspectionAttachment(
        this.inspectionId,
        file,
        onUpdateProgress,
        cancelTokenSource
      )();
      runInAction(() => {
        this.attachments = [...this.attachments, data];
      });
    } catch (e) {
      const error = e as AxiosError<{ file: string[] }>;
      const fieldErrors = error.response?.data?.file;
      if (fieldErrors) {
        // Expected validation failure (e.g. file too large) — surface to the user, don't report to Sentry.
        fieldErrors.forEach((message) => this.rootStore.notification.enqueueErrorSnackbar(message));
      } else {
        // Unexpected failure — capture the AxiosError so status/response are available for diagnosis.
        Logger.error(`Invalid add file API result: inspection ID: ${this.inspectionId}.`, error);
        this.rootStore.notification.enqueueErrorSnackbar('Failed to upload file');
      }
    }
  }

  @action.bound
  async deleteFile(fileId: string): Promise<void> {
    this.deletingFile = fileId;
    const deletedAttachment = this.attachments.find((attachment) => attachment.uuid === fileId);

    try {
      await API.workflows.removeInspectionAttachment(this.inspectionId, fileId)();
      runInAction(() => {
        this.attachments = this.attachments.filter((attachment) => attachment.uuid !== fileId);
        this.removeActivity(fileId);

        postMessage(EventType.DeletedFile, {
          inspectionId: this.inspectionId,
          questionId: deletedAttachment?.question_id,
          fileId,
        });
      });
    } catch (e) {
      Logger.error(`Invalid delete file API result: inspection ID: ${this.inspectionId}. File ID: ${fileId}`, e);
    } finally {
      runInAction(() => {
        this.deletingFile = null;
      });
    }
  }

  @observable stale = false;

  @subscribe(EventType.AddedFile)
  @subscribe(EventType.DeletedFile)
  @action
  invalidateAttachments(): void {
    this.stale = true;
  }

  @action.bound
  async loadAttachments(): Promise<void> {
    if ((this.attachments.length > 0 && !this.stale) || this.loadingAttachments) {
      return;
    }

    this.stale = false;

    this.loadingAttachments = true;

    try {
      const {
        data: { results },
      } = await API.workflows.loadInspectionAttachments(this.inspectionId)();

      runInAction(() => {
        this.attachments = results as FileActivityResponse[];
        this.activities = results;
      });
    } catch (e) {
      Logger.error(`Invalid load attachments API result: inspection ID: ${this.inspectionId}.`, e);
    } finally {
      runInAction(() => {
        this.loadingAttachments = false;
      });
    }
  }
}
